import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthorizationDTO, PhysicianNew } from '@core/model';
import { AuthenticationService, PhysicianService } from '@core/service';
import { PhysicianAccountPgpsDTO } from '@mynexus/mynexus-ui-lib';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-auth-request-physician',
    templateUrl: './auth-request-physician.component.html',
    styleUrls: ['./auth-request-physician.component.scss'],
    standalone: false
})
export class AuthRequestPhysicianComponent implements OnInit {
  @Input() requestType: string;
  @Input() orderingPhysician: PhysicianAccountPgpsDTO;
  @Input() followingPhysician: PhysicianAccountPgpsDTO;
  @Input() npiNotFoundOrdering = false;
  @Input() npiNotFoundFollowing = false;
  @Input() isNewOrderingFormValid = false;
  @Input() newOrderingPhysician: any;
  @Input() newFollowingPhysician: any;
  @Input() sameAsOrdering = false;
  @Input() isHhAuthForPacUser = false;
  @Input() authorizationDTO: AuthorizationDTO = null;

  @Output() orderingPhysicianChange = new EventEmitter<PhysicianAccountPgpsDTO>();
  @Output() followingPhysicianChange = new EventEmitter<PhysicianAccountPgpsDTO>();
  @Output() npiNotFoundOrderingChange = new EventEmitter<boolean>();
  @Output() npiNotFoundFollowingChange = new EventEmitter<boolean>();
  @Output() newOrderingPhysicianChange = new EventEmitter<any>();
  @Output() newFollowingPhysicianChange = new EventEmitter<any>();
  @Output() sameAsOrderingChange = new EventEmitter<boolean>();
  @Output() isStepFormValid = new EventEmitter<boolean>();
  @Output() isNewOrderingFormValidChange =  new EventEmitter<boolean>();

  tenantId: string;
  isExternalUser: boolean;
  initialRequestType = 'Initial';
  subsequentRequestType = 'Subsequent';
  orderingPhysicianType = 'Ordering';
  followingPhysicianType = 'Following';
  orderingPhysicianList: PhysicianAccountPgpsDTO[] = [];
  followingPhysicianList: PhysicianAccountPgpsDTO[] = [];
  newOrderingPhysicianData: any;
  isOrderingSelectViewMode  = false;
  isFollowingSelectViewMode  = false;
  isOrderingPhysicianNotFound = false;
  isFollowingPhysicianNotFound = false;
  isNewFollowingFormValid = false;
  physicianTypeForm: FormGroup;
  physicianViewMode = 'Ordering';
  resetForm: PhysicianNew;
  showAddNewProvider = false;

  constructor(
    private authenticationService: AuthenticationService,
    private route: ActivatedRoute,
    private readonly physicianService: PhysicianService,
    private readonly destroyRef: DestroyRef,
  ) { }

  ngOnInit() {
    this.setGlobalVariables();
    this.resetSelection();
    this.setFormValidity();
    this.setPhysicianToView();
  }

  setPhysicianToView() {
    if (this.requestType === this.subsequentRequestType) {
      this.physicianViewMode = this.followingPhysicianType;

      this.physicianTypeForm = new FormGroup({
        physicianType: new FormControl(this.followingPhysicianType)
      });
      this.physicianControlWatcher();
    } else {
      this.physicianViewMode = this.orderingPhysicianType;
    }
  }

  physicianControlWatcher() {
    this.physicianTypeForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        this.physicianViewMode = res.physicianType;
      });
  }

  setGlobalVariables() {
    this.tenantId = this.route.snapshot.params.tenantId;
    this.isExternalUser = this.authenticationService.isExternalUser;
  }

  setFormValidity() {
    const orderingPhysId = !!this.orderingPhysician?.physicianAccountDTO?.physicianAccountId;
    const followingPhysId = !!this.followingPhysician?.physicianAccountDTO.physicianAccountId;
    let isValid = false;
 
    if (this.isHhAuthForPacUser) {
      isValid = !!(orderingPhysId || this.isNewOrderingFormValid);
    } else {
      const isOrderingPhysValid = this.isExternalUser
        ? orderingPhysId || (this.npiNotFoundOrdering && this.isNewOrderingFormValid)
        : orderingPhysId || (this.npiNotFoundOrdering);
  
      const isFollowingPhysValid = followingPhysId || (this.npiNotFoundFollowing && this.isNewFollowingFormValid);
  
      isValid = this.isExternalUser && this.requestType === this.subsequentRequestType
        ? isOrderingPhysValid && isFollowingPhysValid
        : isOrderingPhysValid;
    }

    this.isStepFormValid.emit(isValid);
  }


  handleOrderingPhysicianNpiNotFound(event: boolean) {
    this.npiNotFoundOrdering = event;
    this.npiNotFoundOrderingChange.emit(event);
    this.setFormValidity();
  }

  handleFollowingPhysicianNpiNotFound(event: boolean) {
    this.npiNotFoundFollowing = event;
    this.npiNotFoundFollowingChange.emit(event);
    this.setFormValidity();
  }

  handleSelectOrderingPhysician(physician: PhysicianAccountPgpsDTO) {
    this.orderingPhysician = physician;
    this.orderingPhysicianChange.emit(physician);
    this.isOrderingSelectViewMode = false;
    this.setFormValidity();
  }

  handleSelectFollowingPhysician(physician: PhysicianAccountPgpsDTO) {
    this.followingPhysician = physician;
    this.followingPhysicianChange.emit(physician);
    this.isFollowingSelectViewMode = false;
    this.setFormValidity();
  }

  handleOrderingPhysicianResultList(physicianList: PhysicianAccountPgpsDTO[]) {
    this.isOrderingPhysicianNotFound = !physicianList?.length;
    this.isOrderingSelectViewMode  = physicianList?.length > 1;
    this.orderingPhysicianList = physicianList;

    if (physicianList?.length === 1) {
      this.orderingPhysician = physicianList[0];
    } else {
      this.orderingPhysician = null;
      this.orderingPhysicianList = physicianList;
    }

    if (this.isOrderingPhysicianNotFound) {
      const physician = this.isHhAuthForPacUser ? null : new PhysicianNew();

      this.newOrderingPhysicianChange.emit(physician);
      this.resetForm = physician;
    } else {
      this.newOrderingPhysicianChange.emit(null);
      this.npiNotFoundOrderingChange.emit(false);
    }

    this.orderingPhysicianChange.emit(this.orderingPhysician);
    this.sameAsOrderingChange.emit(false);
    this.setFormValidity();
  }

  handleAddNewOrderingPhysician(physicianData) {

    this.newOrderingPhysicianChange.emit(physicianData.physician);

    if (physicianData.isValid !== this.isNewOrderingFormValid) {
      this.isNewOrderingFormValid = physicianData.isValid;
      this.isNewOrderingFormValidChange.emit(this.isNewOrderingFormValid);
      this.setFormValidity();
    }
  }

  handleFollowingPhysicianResultList(physicianList: PhysicianAccountPgpsDTO[]) {
    this.isFollowingPhysicianNotFound = !physicianList?.length;
    this.isFollowingSelectViewMode = physicianList?.length > 1;
    this.followingPhysicianList = physicianList;

    if (physicianList?.length === 1) {
      this.followingPhysician = physicianList[0];
    } else {
      this.followingPhysician = null;
      this.followingPhysicianList = physicianList;
    }

    if (this.isFollowingPhysicianNotFound) {
      const physician = new PhysicianNew();

      this.newFollowingPhysicianChange.emit(physician);
      this.resetForm = physician;
    } else {
      this.newFollowingPhysicianChange.emit(null);
      this.npiNotFoundFollowingChange.emit(false);
    }

    this.followingPhysicianChange.emit(this.followingPhysician);
    this.setFormValidity();
  }

  handleAddNewFollowingPhysician(physicianData) {
    this.newFollowingPhysicianChange.emit(physicianData.physician);

    if (physicianData.isValid !== this.isNewFollowingFormValid) {
      this.isNewFollowingFormValid = physicianData.isValid;
      this.setFormValidity();
    }
  }

  handleSameAsOrderingPhysician(isSame: boolean) {
    if (this.sameAsOrdering === isSame ) {
      return;
    }

    if (isSame) {
      this.followingPhysician = this.orderingPhysician;
      this.newFollowingPhysician = this.newOrderingPhysician ? {...this.newOrderingPhysician} : null;
      this.isFollowingPhysicianNotFound = this.isOrderingPhysicianNotFound;
      this.isNewFollowingFormValid = this.isNewOrderingFormValid;
    } else {
      this.followingPhysician = null;
      this.newFollowingPhysician = null;
      this.isFollowingPhysicianNotFound = false;
      this.isNewFollowingFormValid = false;
    }

    this.isFollowingSelectViewMode = false;
    this.sameAsOrderingChange.emit(isSame);
    this.followingPhysicianChange.emit(this.followingPhysician);
    this.newFollowingPhysicianChange.emit(this.newFollowingPhysician);
    this.setFormValidity();
  }

  isEmptyObj(object: object | null | undefined): boolean {
    return !object || Object.keys(object).length === 0;
  }

  private resetSelection(): void {
    if (this.isHhAuthForPacUser && this.authorizationDTO?.orderingPhysicianAccountID && !this.orderingPhysician) {
      this.physicianService.getPhysicianAccountPgp_ByIds(this.authorizationDTO.orderingPhysicianAccountID, this.authorizationDTO.orderingPhysicianPGPID, this.tenantId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(res => {
          this.handleSelectOrderingPhysician({physicianAccountDTO: res.physicianAccountDTO, physicianPgpDTOList: [res.physicianPgpDTO]});
      });
    }
  }
}
